import type { CSSProperties } from 'react'
import type { SelectOption } from '@/components/SearchableSelect'
import type { BackfillApplyMonthSummary } from '@/services/cloverBackfillApply'
import type {
  BackfillAuditDecision,
  BackfillAuditLineDiffChanged,
  BackfillAuditListItem,
  BackfillAuditStatus,
} from '@/services/cloverBackfillAudit'
import type { FieldDefinition, FieldOption } from '@/tableControls'

export const BACKFILL_AUDIT_PAGE_KEY = 'clover-backfill-audit'
export const BACKFILL_AUDIT_DEFAULT_FROM_DATE = '2025-04-01'
export const BACKFILL_AUDIT_DEFAULT_MONTH = '2025-04'

/** First/last calendar day for a YYYY-MM report month. */
export function monthToDateRange(yyyyMm: string): { from: string; to: string } {
  const [yearStr, monthStr] = yyyyMm.split('-')
  const year = Number(yearStr)
  const month = Number(monthStr)
  const mm = String(month).padStart(2, '0')
  const lastDay = new Date(year, month, 0).getDate()
  return {
    from: `${year}-${mm}-01`,
    to: `${year}-${mm}-${String(lastDay).padStart(2, '0')}`,
  }
}

/** Every YYYY-MM from startYm through endYm inclusive, newest first. */
export function backfillAuditMonthRange(startYm: string, endYm: string): string[] {
  if (endYm < startYm) return []
  const months: string[] = []
  let year = Number(startYm.slice(0, 4))
  let month = Number(startYm.slice(5, 7))
  while (true) {
    const ym = `${year}-${String(month).padStart(2, '0')}`
    months.push(ym)
    if (ym === endYm) break
    month += 1
    if (month > 12) {
      month = 1
      year += 1
    }
  }
  return months.reverse()
}

export function backfillAuditMonthOptions(
  updatableThrough: string | null | undefined,
): Array<{ id: string; label: string }> {
  if (!updatableThrough) return []
  const endYm = updatableThrough.slice(0, 7)
  return backfillAuditMonthRange(BACKFILL_AUDIT_DEFAULT_MONTH, endYm).map((id) => ({
    id,
    label: id,
  }))
}

export function isBackfillMonthApplyComplete(
  month: BackfillApplyMonthSummary | null | undefined,
): boolean {
  const run = month?.last_run
  return run?.mode === 'live' && run.status === 'completed'
}

export function mergeBackfillAuditMonthSelectOptions(
  updatableThrough: string | null | undefined,
  applyMonths: BackfillApplyMonthSummary[],
): SelectOption[] {
  const byMonth = new Map(applyMonths.map((entry) => [entry.report_month, entry]))
  return backfillAuditMonthOptions(updatableThrough).map((option) => ({
    ...option,
    status: isBackfillMonthApplyComplete(byMonth.get(option.id)) ? 'complete' : 'incomplete',
  }))
}

export const DECISION_OPTIONS: Array<{ value: BackfillAuditDecision; label: string }> = [
  { value: 'undecided', label: 'Undecided' },
  { value: 'auto', label: 'Auto' },
  { value: 'by_hand', label: 'By hand' },
  { value: 'done_by_hand', label: 'Done by hand' },
  { value: 'skip', label: 'Skip' },
  { value: 'needs_code_change', label: 'Needs code change' },
  { value: 'closed_year', label: 'Closed year' },
  { value: 'applied', label: 'Applied' },
]

const SELECTABLE_DECISION_VALUES = new Set<BackfillAuditDecision>([
  'undecided',
  'auto',
  'by_hand',
  'skip',
  'needs_code_change',
])

export function decisionSelectOptions(
  current: BackfillAuditDecision,
): Array<{ value: BackfillAuditDecision; label: string }> {
  if (current === 'applied') return []
  if (current === 'closed_year') {
    return DECISION_OPTIONS.filter((option) => option.value === 'closed_year')
  }
  const options = DECISION_OPTIONS.filter(
    (option) =>
      SELECTABLE_DECISION_VALUES.has(option.value) ||
      (option.value === 'done_by_hand' && (current === 'by_hand' || current === 'done_by_hand')),
  )
  return options
}

export const STATUS_OPTIONS: FieldOption[] = [
  { value: 'identical', label: 'Identical' },
  { value: 'differs', label: 'Differs' },
  { value: 'missing_in_qbo', label: 'Missing in QBO' },
  { value: 'duplicate_in_qbo', label: 'Duplicate in QBO' },
  { value: 'rebuild_failed', label: 'Rebuild failed' },
  { value: 'no_activity', label: 'No activity' },
]

export const CATEGORY_OPTIONS: FieldOption[] = [
  { value: 'gift_card_only', label: 'Gift card only' },
  { value: 'deposit_window', label: 'Deposit window' },
  { value: 'deposit_mismatch', label: 'Deposit mismatch' },
  { value: 'sales_tax_tips', label: 'Sales tax / tips' },
  { value: 'ar_classification', label: 'A/R classification' },
  { value: 'manual_correction', label: 'Manual correction' },
  { value: 'processor_unavailable', label: 'Processor unavailable' },
  { value: 'unbalanced_rebuild', label: 'Unbalanced rebuild' },
  { value: 'date_differs', label: 'Date differs' },
  { value: 'doc_number_legacy', label: 'Doc number legacy' },
]

const STORE_OPTIONS: FieldOption[] = [
  { value: 'River Oaks', label: 'River Oaks' },
  { value: 'Highland Park', label: 'Highland Park' },
]

const DECISION_FIELD_OPTIONS: FieldOption[] = DECISION_OPTIONS.map((option) => ({
  value: option.value,
  label: option.label,
}))

export const DEFAULT_WIDTHS: Record<string, number> = {
  expand: 40,
  report_date: 110,
  store_name: 120,
  status: 110,
  categories: 180,
  delta_debits: 100,
  diff_summary: 260,
  decision: 170,
  decision_note: 160,
}

export const PINNED_COLUMNS = ['report_date', 'store_name'] as const

export const PINNED_DIVIDER_KEY = 'store_name'

export const COLUMN_KEYS = [
  'expand',
  'report_date',
  'store_name',
  'status',
  'categories',
  'delta_debits',
  'diff_summary',
  'decision',
  'decision_note',
] as const

export type BackfillAuditColumnKey = (typeof COLUMN_KEYS)[number]

export const COLUMN_LABELS: Record<BackfillAuditColumnKey, string> = {
  expand: '',
  report_date: 'Date',
  store_name: 'Store',
  status: 'Status',
  categories: 'Categories',
  delta_debits: 'Δ debits',
  diff_summary: 'Diff summary',
  decision: 'Decision',
  decision_note: 'Note',
}

const SORTABLE_COLUMN_KEYS = new Set<BackfillAuditColumnKey>([
  'report_date',
  'store_name',
  'status',
  'delta_debits',
  'decision',
])

export const DEFAULT_SORT = [{ field: 'report_date', direction: 'desc' as const }]

export const FIELD_DEFINITIONS: FieldDefinition[] = [
  {
    key: 'report_date',
    label: COLUMN_LABELS.report_date,
    type: 'date',
    sortable: true,
    accessor: (row: unknown) => (row as BackfillAuditListItem).report_date,
  },
  {
    key: 'store_name',
    label: COLUMN_LABELS.store_name,
    type: 'multiSelect',
    sortable: true,
    filterable: true,
    options: STORE_OPTIONS,
    accessor: (row: unknown) => (row as BackfillAuditListItem).store_name,
  },
  {
    key: 'status',
    label: COLUMN_LABELS.status,
    type: 'multiSelect',
    sortable: true,
    filterable: true,
    options: STATUS_OPTIONS,
    accessor: (row: unknown) => (row as BackfillAuditListItem).status,
  },
  {
    key: 'categories',
    label: COLUMN_LABELS.categories,
    type: 'multiSelect',
    filterable: true,
    options: CATEGORY_OPTIONS,
    accessor: (row: unknown) => (row as BackfillAuditListItem).categories,
  },
  {
    key: 'delta_debits',
    label: COLUMN_LABELS.delta_debits,
    type: 'number',
    sortable: true,
    accessor: (row: unknown) => {
      const value = (row as BackfillAuditListItem).delta_debits
      if (value == null || value === '') return null
      const num = typeof value === 'string' ? Number(value) : value
      return Number.isFinite(num) ? num : null
    },
  },
  {
    key: 'diff_summary',
    label: COLUMN_LABELS.diff_summary,
    type: 'text',
    accessor: (row: unknown) => (row as BackfillAuditListItem).diff_summary ?? '',
  },
  {
    key: 'decision',
    label: COLUMN_LABELS.decision,
    type: 'multiSelect',
    sortable: true,
    filterable: true,
    options: DECISION_FIELD_OPTIONS,
    accessor: (row: unknown) => (row as BackfillAuditListItem).decision,
  },
  {
    key: 'decision_note',
    label: COLUMN_LABELS.decision_note,
    type: 'text',
    accessor: (row: unknown) => (row as BackfillAuditListItem).decision_note ?? '',
  },
]

export function buildBackfillAuditFilterFields(): FieldDefinition[] {
  return FIELD_DEFINITIONS.filter((field) => field.filterable)
}

export function isBackfillAuditColumnSortable(key: BackfillAuditColumnKey): boolean {
  return SORTABLE_COLUMN_KEYS.has(key)
}

export function isBackfillAuditPinnedColumn(columnKey: string): boolean {
  return PINNED_COLUMNS.includes(columnKey as (typeof PINNED_COLUMNS)[number])
}

export function pinnedHeaderClass(columnKey: string): string {
  if (!isBackfillAuditPinnedColumn(columnKey)) return ''
  const classes = ['table-sticky-pinned', 'table-sticky-cell']
  if (columnKey === PINNED_DIVIDER_KEY) classes.push('table-locked-edge')
  return classes.join(' ')
}

export function pinnedCellClass(columnKey: string): string {
  if (!isBackfillAuditPinnedColumn(columnKey)) return ''
  const classes = ['table-sticky-pinned', 'table-sticky-cell']
  if (columnKey === PINNED_DIVIDER_KEY) classes.push('table-locked-edge')
  return classes.join(' ')
}

export function fixedColumnStyle(width: number): CSSProperties {
  return {
    width,
    minWidth: width,
    maxWidth: width,
    boxSizing: 'border-box',
  }
}

function formatSignedAmount(value: number): string {
  const rounded = Math.round(value * 100) / 100
  const text = Math.abs(rounded).toFixed(2)
  if (rounded > 0) return `+${text}`
  if (rounded < 0) return `−${text}`
  return '0.00'
}

export function formatDiffSummary(changed: BackfillAuditLineDiffChanged[]): string {
  if (!changed.length) return ''
  return changed
    .map((entry) => {
      const name = entry.account_name ?? entry.key ?? 'Unknown'
      const delta = entry.delta
      if (delta == null || delta === '') return name
      const num = typeof delta === 'string' ? Number(delta) : delta
      if (!Number.isFinite(num)) return `${name} ${String(delta)}`
      return `${name} ${formatSignedAmount(num)}`
    })
    .join(' · ')
}

export function suggestedDecisionFromCategories(
  categories: string[],
  status: BackfillAuditStatus,
  reportDate: string,
  closedThrough: string,
): BackfillAuditDecision {
  if (reportDate <= closedThrough) return 'closed_year'
  if (status === 'identical' || status === 'no_activity') return 'skip'
  const set = new Set(categories)
  if (
    set.has('manual_correction') ||
    set.has('deposit_mismatch') ||
    set.has('ar_classification')
  ) {
    return 'by_hand'
  }
  if (
    set.has('deposit_window') ||
    set.has('processor_unavailable') ||
    set.has('unbalanced_rebuild')
  ) {
    return 'needs_code_change'
  }
  if (set.has('gift_card_only')) return 'auto'
  if (set.has('sales_tax_tips') || set.has('date_differs') || set.has('doc_number_legacy')) {
    return 'by_hand'
  }
  return 'undecided'
}

export function suggestedDecisionLabel(
  decision: BackfillAuditDecision | null | undefined,
): string {
  if (!decision) return '—'
  return DECISION_OPTIONS.find((option) => option.value === decision)?.label ?? decision
}

export function formatBackfillMoney(value: number | string | null | undefined): string {
  if (value == null || value === '') return '—'
  const num = typeof value === 'string' ? Number(value) : value
  if (!Number.isFinite(num)) return String(value)
  return num.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/** Accounting format: $#,##0.00;($#,##0.00);- */
export function formatBackfillAccountingMoney(
  value: number | string | null | undefined,
): string {
  if (value == null || value === '') return '—'
  const num = typeof value === 'string' ? Number(value) : value
  if (!Number.isFinite(num)) return String(value)
  if (num === 0) return '-'
  const formatted = Math.abs(num).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return num < 0 ? `($${formatted})` : `$${formatted}`
}

export function backfillAuditRunButtonLabel(input: {
  auditMonth: string | null
  hasAuditRows: boolean
  startedThisSession: boolean
  isRunning?: boolean
}): string {
  if (input.isRunning) return 'Running…'
  if (!input.auditMonth) return 'Run audit'
  if (input.startedThisSession || input.hasAuditRows) return 'Refresh Audit'
  return 'Run audit'
}
